import { Vector2 } from "../../../../lib/math";
import { ewent, observe } from "../../../../lib/reactive";
import { Scans } from "../../../cluetheory/Scans";
import { Alt1Color } from "../../../../lib/alt1/Alt1Color";
import { deps } from "../../../dependencies";
import { Alt1 } from "../../../../lib/alt1/Alt1";
import { ClueTrainerWiki } from "../../../wiki";
import { ScanSolving } from "./ScanSolving";
import { Alt1Overlay } from "../../../../lib/alt1/overlay/Alt1Overlay";
import { Alt1OverlayButton } from "../../../../lib/alt1/overlay/Alt1OverlayButton";
import Behaviour from "../../../../lib/ui/Behaviour";
import { FakeLodash } from "../../../../lib/coreutil/FakeLodash";
export class ScanControlPrototype extends Behaviour {
    constructor(panel_reader) {
        super();
        this.panel_reader = panel_reader;
        this.actual_overlay = this.withSub(new ScanControlPrototype.Overlay(deps().app.settings.settings.solving.scans.input_control_configuration)
            .setVisible(deps().app.settings.settings.solving.scans.input_control_enabled));
        this.visible = this.actual_overlay.visible;
        panel_reader.onStateChange(s => this.actual_overlay.setScanPanelState(s));
        this.actual_overlay.setScanPanelState(this.panel_reader.getState());
    }
    setActiveNode(node) {
        this.actual_overlay.setNode(node);
    }
    onNodeSelection(f) {
        this.actual_overlay.node_selection.on(f);
    }
    begin() {
    }
    end() {
    }
}
(function (ScanControlPrototype) {
    class Overlay extends Alt1Overlay {
        constructor(config) {
            super();
            this.config = config;
            this.single = null;
            this.double = null;
            this.node = null;
            this.panel_state = null;
            this.node_selection = ewent();
            this.pulse_buttons = [];
            this.back_button = null;
            this.help_button = null;
            this.createButtons();
        }
        createButtons() {
            const all_options = [
                [
                    { pulse: 1, different_level: false },
                    { pulse: 2, different_level: false },
                    { pulse: 3, different_level: false }
                ],
                [
                    { pulse: 1, different_level: true },
                    { pulse: 2, different_level: true },
                    { pulse: 3, different_level: true }
                ]
            ];
            this.pulse_buttons = all_options.map(r => r.map(p => new Overlay.PulseButton(p).addTo(this)));
            this.pulse_buttons.flat().forEach(b => b
                .interactivity().main_hotkey_pressed.on(() => this.node_selection.trigger(determineChild(this.node, b.pulse))));
            this.back_button = new Alt1OverlayButton(null, {
                style: {
                    stroke: { width: 1, color: Alt1Color.white },
                    constrast: { width: 1, color: Alt1Color.black },
                    font: {
                        width: 16,
                        color: Alt1Color.white,
                        centered: true
                    }
                },
                active_style: {
                    stroke: { width: 2, color: Alt1Color.white },
                    font: {
                        width: 18,
                        color: Alt1Color.white,
                        centered: true
                    }
                }
            }).addTo(this);
            this.help_button = new Alt1OverlayButton(null, {
                text: "?",
                style: {
                    stroke: { width: 1, color: Alt1Color.white },
                    constrast: { width: 1, color: Alt1Color.black },
                    font: {
                        width: 14,
                        color: Alt1Color.white,
                        centered: true
                    }
                },
                active_style: {
                    stroke: { width: 2, color: Alt1Color.white },
                    constrast: { width: 1, color: Alt1Color.black },
                    font: {
                        width: 18,
                        color: Alt1Color.white,
                        centered: true
                    }
                }
            }).addTo(this);
            this.help_button.interactivity().setTooltip("(Alt+1) for an explanation of this interactive overlay.");
            this.help_button.interactivity().main_hotkey_pressed.on(() => {
                ClueTrainerWiki.openOnPage("scantreecontroloverlay");
            });
            this.back_button.interactivity().main_hotkey_pressed.on(e => { if (this.node.parent)
                this.node_selection.trigger(this.node.parent.node); });
        }
        begin() {
            super.begin();
            Overlay.registerActive(this);
        }
        setNode(node) {
            this.node = node;
            this.back_button.setVisible(!!this.node.parent);
            this.pulse_buttons.forEach(row => row.forEach(button => {
                button.setRelevant(!!determineChild(this.node, button.pulse));
            }));
            this.updatePossible();
            this.rerender();
        }
        setScanPanelState(state) {
            const meerkats_changed = this.panel_state?.meerkats != state.meerkats;
            this.panel_state = { ...state };
            this.pulse_buttons.forEach(row => row.forEach(button => {
                const impossible = button.pulse.pulse == 3 != this.panel_state.triple
                    || button.pulse.different_level != this.panel_state.different_level;
                button.setPossible(!impossible);
            }));
            this.updatePossible();
            if (meerkats_changed)
                this.rerender();
        }
        updatePossible() {
            const possibles = this.pulse_buttons.flat().filter(b => b.isRelevant() && b.isPossible());
            if (possibles.length == 1)
                possibles[0].interactivity().makeDefaultHotkeyHandler();
            else
                Alt1Overlay.Interactivity.setDefaultMainHotkeyHandler(null);
        }
        renderWithBuilder(overlay) {
            if (!this.node)
                return;
            const UPPER_ROW_HEIGHT = 25;
            const GUTTER = this.config.gutter;
            const origin = Vector2.add(this.config.position, { x: Alt1.clientSize().x / 2 - this.config.size.x / 2, y: 0 });
            const small_back_button = this.config.force_small_back_button || this.config.size.x < 280;
            const back_button_width = small_back_button ? 25 : 80;
            this.back_button.updateConfig(c => c.text = small_back_button ? "<" : "< Back");
            this.back_button.setPosition({
                origin: origin,
                size: { x: back_button_width, y: UPPER_ROW_HEIGHT }
            });
            this.help_button.setPosition(this.config.show_help_button ? {
                origin: { x: origin.x + this.config.size.x - back_button_width, y: this.config.position.y },
                size: { x: back_button_width, y: UPPER_ROW_HEIGHT }
            } : null);
            const progress = 1 - (Math.log2(this.node.remaining_candidates.length) / Math.log2(this.node.root.root_node.remaining_candidates.length));
            const PROGRESS_BAR_HEIGHT = 2;
            const PROGRESS_BAR_BORDER = 1;
            const PROGRESS_BAR_TOTAL_HEIGHT = PROGRESS_BAR_BORDER + PROGRESS_BAR_HEIGHT;
            const progress_bar_space = { x: this.config.size.x - 2 * this.back_button.position().size.x, y: UPPER_ROW_HEIGHT };
            const progress_bar_center = Vector2.add(origin, { x: this.back_button.position().size.x, y: 0 }, Vector2.scale(0.5, { x: progress_bar_space.x, y: PROGRESS_BAR_TOTAL_HEIGHT }));
            const meerkats_assumed = this.node.root.raw.assumed_range == (this.node.root.clue.range + 5);
            overlay.progressbar(progress_bar_center, progress_bar_space.x - 2 * GUTTER, progress, PROGRESS_BAR_HEIGHT, PROGRESS_BAR_BORDER);
            overlay.text(this.node.remaining_candidates.length != 1 ? `${this.node.remaining_candidates.length} spots` : "1 spot", Vector2.add(progress_bar_center, {
                x: 0,
                y: 12
            }), {
                width: 12,
                color: Alt1Color.fromHex("#FFFFFF"),
            });
            const meerkat_warning = this.config.warn_for_meerkats && meerkats_assumed != this.panel_state.meerkats;
            if (meerkat_warning) {
                const text = this.panel_state.meerkats
                    ? "Do not use meerkats for this scan tree!"
                    : "Meerkats required for this scan tree!";
                overlay.text(text, Vector2.add(progress_bar_center, {
                    x: 0,
                    y: 12 + UPPER_ROW_HEIGHT
                }), {
                    width: 14,
                    color: Alt1Color.red,
                });
            }
            const visible_buttons = this.pulse_buttons.map(row => row.filter(b => b.isRelevant())).filter(r => r.length > 0);
            const rows_n = visible_buttons.length;
            const row_height = (this.config.size.y - (rows_n - 1) * GUTTER) / rows_n;
            const options_origin = Vector2.add(origin, { x: 0, y: UPPER_ROW_HEIGHT + GUTTER + (meerkat_warning ? UPPER_ROW_HEIGHT : 0) });
            const context = visible_buttons.flat().map(b => b.pulse);
            visible_buttons.forEach((row, row_i) => {
                const column_n = row.length;
                const column_width = (this.config.size.x - (column_n - 1) * GUTTER) / column_n;
                const row_origin = Vector2.add(options_origin, { x: 0, y: row_i * (row_height + GUTTER) });
                row.forEach((option, column_i) => {
                    const option_origin = Vector2.add(row_origin, { x: column_i * (column_width + GUTTER), y: 0 });
                    const rect = {
                        origin: option_origin,
                        size: { x: column_width, y: row_height }
                    };
                    option.setPositionAndContext(rect, context);
                });
            });
        }
        setConfig(config) {
            this.config = FakeLodash.cloneDeep(config);
            this.rerender();
        }
    }
    ScanControlPrototype.Overlay = Overlay;
    (function (Overlay) {
        let active_overlay = null;
        function registerActive(overlay) {
            active_overlay = overlay;
        }
        Overlay.registerActive = registerActive;
        function getActive() {
            if (active_overlay?.isActive())
                return active_overlay;
            return null;
        }
        Overlay.getActive = getActive;
        var simplify_with_context = Scans.Pulse.simplify_with_context;
        class PulseButton extends Alt1OverlayButton {
            constructor(pulse) {
                super(null, {
                    style: {
                        stroke: { width: 2, color: ScanSolving.PulseColors.forPulse(pulse) },
                        constrast: { width: 1, color: Alt1Color.black },
                        font: {
                            width: 12,
                            color: Alt1Color.white,
                            shadow: true
                        }
                    },
                    active_style: {
                        stroke: { width: 4, color: ScanSolving.PulseColors.forPulse(pulse) },
                    }
                });
                this.pulse = pulse;
                this.possible = observe(true);
                this.relevant = observe(true);
                this.possible.subscribe(v => {
                    // this.updateConfig(c => c.style.stroke.width = v ? 3 : 1)
                });
                this.relevant.subscribe(v => this.setVisible(v));
            }
            renderWithBuilder(overlay) {
                super.renderWithBuilder(overlay);
                if (!this.area)
                    return;
                if (this.interactivity().is_default_action.value()) {
                    overlay.text("Auto", Vector2.add(this.area.origin, { x: this.area.size.x - 20, y: 10 }), {
                        color: Alt1Color.white,
                        centered: true,
                        width: 8
                    });
                }
            }
            setRelevant(v) {
                this.relevant.set(v);
            }
            isRelevant() {
                return this.relevant.value();
            }
            setPossible(v) {
                this.possible.set(v);
            }
            isPossible() {
                return this.possible.value();
            }
            setPositionAndContext(position, context) {
                this.context = simplify_with_context(this.pulse, context);
                const txt = text[this.context.text];
                const config = this.config.value();
                if (txt) {
                    if (position.size.x > 100)
                        config.text = (txt.long);
                    else
                        config.text = (txt.short);
                }
                else
                    config.text = null;
                const color = ScanSolving.PulseColors.forContextPulse(this.context);
                config.style.stroke.color = color;
                config.active_style.stroke.color = color;
                this.setPosition(position);
            }
        }
        Overlay.PulseButton = PulseButton;
    })(Overlay = ScanControlPrototype.Overlay || (ScanControlPrototype.Overlay = {}));
    const text = {
        DL: { long: "Different Level", short: "DL" }, TF: { long: "Too Far", short: "TF" }
    };
    function determineChild(node, info) {
        {
            const candidates = node.children.filter(c => {
                return (info.pulse == undefined || c.key.pulse == info.pulse) &&
                    (info.different_level == undefined || (c.key.different_level ?? false) == (info.different_level ?? false));
            });
            if (candidates.length == 1)
                return candidates[0].value;
        }
        return undefined;
    }
    ScanControlPrototype.determineChild = determineChild;
})(ScanControlPrototype || (ScanControlPrototype = {}));
